import { Divider, Grid, makeStyles, Typography } from '@material-ui/core';
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import InputAdornmentChip from 'components/InputAdornmentChip';
import { Formik } from 'formik';
import i18n from 'i18n';
import Button from 'components/Button';
import Permission from 'components/ValidPermission';
import {
  FormRequestingUserReconnectionProps,
  ServerFields,
} from 'modules/environments/interfaces';
import { publishNewImageRequest } from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { useParams } from 'react-router';
import { ModalFields } from 'modules/modal/interfaces';
import GenericForm from 'skyone-lib/lib/GenericForm';
import DynamicTextField from 'components/DynamicTextField';
import PageTitle from 'components/PageTitle';
import ImagesLogs from 'containers/Environments/ListServers/PreparationServer/ImagesLogs';
import styles from './styles';
import schema from './schema';
import screenFields from './screenFields';

const useStyles = makeStyles(styles);

interface Props {
  preparationServer: ServerFields;
  highlightSection?: string;
  publishNewImageRequest: ({
    environmentUuid,
    instanceId,
    values,
    type,
  }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
}

const ReconnectionPublication = ({
  preparationServer,
  highlightSection,
  publishNewImageRequest,
  modalOpen,
  modalClose,
  setCurrentTab,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();

  const sectionRef = useRef<HTMLDivElement>(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  if (highlightSection === 'logs') {
    scrollToSection();
  }

  const initialValues: FormRequestingUserReconnectionProps = {
    disconnection_time: '5',
    operator_name: '',
    notification_email: [],
    user_message: '',
  };

  const handleConfirmPublish = values => {
    modalOpen({
      title: i18n.t('RECONNECTION_PUBLICATION_KEY'),
      maxWidth: 'sm',
      footer: (
        <Grid>
          <Button
            id="submit-button"
            submitButton
            onClick={() => {
              publishNewImageRequest({
                environmentUuid: id,
                instanceId: preparationServer?.instance_id,
                values,
                type: 'closing_user_sessions',
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
      content: (
        <Typography className={classes.headerText}>
          {i18n.t('RECONNECTION_PUBLICATION_CONFIRM_KEY')}
        </Typography>
      ),
    });
  };

  return (
    <>
      <Grid container className={classes.formGrid}>
        <Grid container className={classes.header}>
          <Typography className={classes.headerText}>
            {i18n.t(
              'ENVIRONMENTS_PAGE.REQUEST_USER_FEATURE_MSG.TITLE_REQUEST_KEY'
            )}
          </Typography>
          <Typography className={classes.headerText}>
            {i18n.t(
              'ENVIRONMENTS_PAGE.REQUEST_USER_FEATURE_MSG.SUBTITLE_REQUEST_KEY'
            )}
          </Typography>
        </Grid>
        <Grid container>
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={handleConfirmPublish}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid container spacing={2}>
                <InputAdornmentChip
                  {...screenFields[1]}
                  values={values.notification_email}
                  setFieldValue={setFieldValue}
                />
                <GenericForm
                  fields={values}
                  screenFields={screenFields.slice(0, 1)}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <GenericForm
                  fields={values}
                  screenFields={screenFields.slice(2, 3)}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <DynamicTextField
                  {...screenFields[3]}
                  setFieldValue={setFieldValue}
                  value={values?.user_message}
                  errors={errors}
                />
                <Grid container justify="flex-end" className={classes.buttons}>
                  <Button id="submit-button" onClick={submitForm} submitButton>
                    {i18n.t('ENVIRONMENTS_PAGE.PUBLICATIONS_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      </Grid>
      <Grid container>
        <Divider className={classes.divider} />
      </Grid>
      <Grid container>
        <Grid container className={classes.actionsGrid}>
          <Grid item>
            <PageTitle
              title={i18n.t('ENVIRONMENTS_PAGE.LOGS_PUBLICATION_KEY')}
              subtitle={i18n.t('LOGS_PUBLICATION_SUBSCRIPTION_KEY')}
            />
          </Grid>
          <Grid item>
            <Permission value="list_models">
              <Button
                className={classes.seeModelsButtons}
                variant="outlined"
                onClick={() => setCurrentTab(2)}
              >
                {i18n.t('SEE_GENERATED_MODELS_KEY')}
              </Button>
            </Permission>
          </Grid>
        </Grid>
        <Grid container innerRef={sectionRef}>
          <ImagesLogs id={id} instanceId={preparationServer?.instance_id} />
        </Grid>
      </Grid>
    </>
  );
};

export default connect(null, {
  publishNewImageRequest,
  modalOpen,
  modalClose,
})(ReconnectionPublication);
